import { z } from 'zod';

import type { TFunction } from 'i18next';

import { CASHIER_TERMINAL_PROTOCOL } from '@/constants';

// `Form.Select` writes `null` when cleared, so a required select cannot be `z.string().min(1)`
// — that reports zod's own «expected string, received null» instead of the translated message.
export const requiredSelect = (t: TFunction) =>
  z
    .string()
    .nullable()
    .refine((value) => !!value, { message: t('validation_required') });

// A new upload, or the stored URL (or `''` to remove it) — the wrapper sets whether it is required.
export const imageFieldSchema = z.union([z.instanceof(File), z.string()]);

export const customFieldValuesSchema = z.record(
  z.string(),
  z.string().default(''),
);

export const refineClientOrGuest =
  (t: TFunction) =>
  (values: { client_id: string; guest_info: string }, ctx: z.RefinementCtx) => {
    if (!values.client_id && !values.guest_info.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['guest_info'],
        message: t('validation_required'),
      });
    }
  };

export const refineRequiredCustomFields =
  (t: TFunction, requiredCustomFieldIds: number[]) =>
  (
    values: { custom_field_values: Record<string, string> },
    ctx: z.RefinementCtx,
  ) => {
    requiredCustomFieldIds
      .filter((fieldId) => !(values.custom_field_values[fieldId] ?? '').trim())
      .forEach((fieldId) => {
        ctx.addIssue({
          code: 'custom',
          path: ['custom_field_values', String(fieldId)],
          message: t('validation_required'),
        });
      });
  };

export const terminalFormShape = (t: TFunction) => ({
  name: z
    .string({ message: t('validation_required') })
    .min(1, { message: t('validation_required') })
    .trim(),
  location_id: z
    .string({ message: t('validation_required') })
    .min(1, { message: t('validation_required') }),
  mac_address: z
    .string({ message: t('validation_required') })
    .min(1, { message: t('validation_required') })
    .regex(/^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/, {
      message: t('Invalid MAC address format'),
    }),
  model: z.string().optional(),
  bank: z.string().optional(),
  protocol: z
    .string({ message: t('validation_required') })
    .min(1, { message: t('validation_required') }),
  port: z
    .string({ message: t('validation_required') })
    .min(1, { message: t('validation_required') })
    .refine(
      (value) => {
        const port = Number(value);
        return Number.isInteger(port) && port >= 1 && port <= 65535;
      },
      {
        message: t('Port must be between {{min}} and {{max}}', {
          min: 1,
          max: 65535,
        }),
      },
    ),
  merchant_id: z.string().optional(),
  notes: z.string().optional(),
});

export const refineTerminalMerchantId =
  (t: TFunction) =>
  (
    values: { protocol: string; merchant_id?: string },
    ctx: z.RefinementCtx,
  ) => {
    if (
      values.protocol === CASHIER_TERMINAL_PROTOCOL.SSI_JSON &&
      !values.merchant_id?.trim()
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['merchant_id'],
        message: t('Merchant ID is required for SSI JSON terminals'),
      });
    }
  };
